import { type SelectOption } from '@aus-platform/design-system';
import { isEmpty, isNil } from 'lodash';
import { useState } from 'react';
import { type DashboardDataCategoryEntry } from 'shared/api';
import {
  createEmptyLeadDistanceEntry,
  isLeadDistanceEntryValid,
  LEAD_DISTANCE_REQUIRED_FIELDS,
  REQUIRED_FIELD_MESSAGE,
} from './helpers';
import {
  type LeadDistanceEntry,
  type LeadDistanceErrors,
  type LeadDistanceField,
  type LeadDistanceNumberField,
  type LeadDistanceSelectField,
} from './types';

type UseLeadDistancesParams = {
  initialEntries: LeadDistanceEntry[];
  initialDeletedIds: string[];
};

/**
 * Owns the lead-distance card collection: the entries themselves, the ids of
 * saved records queued for deletion, per-card validation errors and the
 * "scroll to newly added card" signal. Keeping this out of the configuration
 * component leaves that component responsible only for rendering.
 */
export const useLeadDistances = ({
  initialEntries,
  initialDeletedIds,
}: UseLeadDistancesParams) => {
  // States.
  const [entries, setEntries] = useState<LeadDistanceEntry[]>(initialEntries);

  const [deletedIds, setDeletedIds] = useState<string[]>(initialDeletedIds);
  const [errors, setErrors] = useState<Record<string, LeadDistanceErrors>>({});
  const [scrollToClientId, setScrollToClientId] = useState<string | null>(null);

  // Variables.
  const areEntriesValid =
    !isEmpty(entries) && entries.every(isLeadDistanceEntryValid);

  // Handlers.
  const updateEntry = (
    clientId: string,
    changes: Partial<LeadDistanceEntry>,
  ) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.clientId === clientId ? { ...entry, ...changes } : entry,
      ),
    );
  };

  const onSelectChange = (
    clientId: string,
    field: LeadDistanceSelectField,
    option: SelectOption<DashboardDataCategoryEntry>,
  ) => {
    updateEntry(clientId, { [field]: option });
  };

  const onNumberChange = (
    clientId: string,
    field: LeadDistanceNumberField,
    value: number | null,
  ) => {
    updateEntry(clientId, { [field]: value });
  };

  const onFieldBlur = (clientId: string, field: LeadDistanceField) => {
    if (!LEAD_DISTANCE_REQUIRED_FIELDS.includes(field)) {
      return;
    }

    const entry = entries.find((item) => item.clientId === clientId);

    setErrors((prev) => ({
      ...prev,
      [clientId]: {
        ...prev[clientId],
        [field]: isNil(entry?.[field]) ? REQUIRED_FIELD_MESSAGE : '',
      },
    }));
  };

  const onFieldFocus = (clientId: string, field: LeadDistanceField) => {
    setErrors((prev) => ({
      ...prev,
      [clientId]: {
        ...prev[clientId],
        [field]: '',
      },
    }));
  };

  const onAddEntry = () => {
    const newEntry = createEmptyLeadDistanceEntry();

    setEntries((prev) => [...prev, newEntry]);
    setScrollToClientId(newEntry.clientId);
  };

  const onRemoveEntry = (clientId: string) => {
    const removedEntry = entries.find((entry) => entry.clientId === clientId);

    if (!isNil(removedEntry?.id)) {
      setDeletedIds((prev) => [...prev, removedEntry.id as string]);
    }

    setEntries((prev) => prev.filter((entry) => entry.clientId !== clientId));

    setErrors((prev) => {
      const next = { ...prev };
      delete next[clientId];
      return next;
    });
  };

  // Clears transient state when the lead-distance section is toggled.
  const resetTransientState = () => {
    setErrors({});
    setDeletedIds([]);
  };

  // Guarantees at least one card exists when the section is enabled.
  const ensureEntryExists = () => {
    setEntries((prev) =>
      isEmpty(prev) ? [createEmptyLeadDistanceEntry()] : prev,
    );
  };

  const clearScrollTarget = () => setScrollToClientId(null);

  return {
    entries,
    deletedIds,
    errors,
    areEntriesValid,
    scrollToClientId,
    onSelectChange,
    onNumberChange,
    onFieldBlur,
    onFieldFocus,
    onAddEntry,
    onRemoveEntry,
    resetTransientState,
    ensureEntryExists,
    clearScrollTarget,
  };
};
